import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Rating,
  InputAdornment,
  Pagination,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ElectricScooter,
  Search,
  VolumeUp,
  Settings,
  Block,
  LockOpen,
  DirectionsBus,
  SatelliteAlt,
  ArrowUpward,
  ArrowDownward,
  LocationOff,
  QrCode2,
  Close,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import CircularProgress from '@mui/material/CircularProgress';
import Title from '../../components/Title';
import ScooterSettingsModal from '../../modals/ScooterSettingsModal';
import ScooterCounters from '../../components/ScooterCounters';
import {
  selectScooters,
  signalScooterAsync,
  updateScooterStatusAsync,
  lockScooterAsync
} from '../../redux/slices/scootersSlice';
import { Scooter } from '../../resources/types/scootersTypes';
import { ScooterStatus, MapFilters } from '../ZonesPage/interface';
import usePermissions from '../../hooks/usePermissions';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { useWebSocketContext } from '../../containers/WebSocketProvider';
import { getDotColor, getDotTooltip } from '../../helpers/scooter-markers-helper';
import { TableCell as MuiTableCell } from '@mui/material';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const LIMIT = 10;

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)'
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? '#EEEEEE' : '#333333',
  }
}));

// Helper functions
const isAVTO = (scooterName: string) => {
  return scooterName.toUpperCase().includes('AVTO');
};

const hasGPSIssue = (scooterName: string) => {
  return scooterName.startsWith('!') && scooterName.endsWith('!');
};

const cleanScooterName = (scooterName: string) => {
  if (hasGPSIssue(scooterName)) {
    return scooterName.slice(1, -1);
  }
  return scooterName;
};

const hasInactivityFlag = (battery: number | string) => {
  return String(battery).includes('*');
};

const hasNoCoordinates = (battery: number | string) => {
  return String(battery).includes('!');
};

const extractScooterNumber = (name: string): string => {
  const cleaned = cleanScooterName(name);
  const match = cleaned.match(/\d{4}/);
  return match ? match[0] : cleaned;
};

const generateSquareQRWithText = async (code: string): Promise<string> => {
  const qrSize = 500;
  const textHeight = 140;
  const totalHeight = qrSize + textHeight;
  
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, code, {
    width: qrSize,
    margin: 2
  });
  
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = totalHeight;
  finalCanvas.height = totalHeight;
  const ctx = finalCanvas.getContext('2d');
  
  if (!ctx) throw new Error('Canvas context not available');
  
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, totalHeight, totalHeight);
  
  const xOffset = (totalHeight - qrSize) / 2;
  ctx.drawImage(qrCanvas, xOffset, 0);
  
  ctx.fillStyle = '#000000';
  ctx.font = 'bold 96px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code, totalHeight / 2, qrSize + textHeight / 2);
  
  return finalCanvas.toDataURL('image/png');
};

const cleanBatteryValue = (battery: number | string) => {
  let batteryStr = String(battery);
  // Видаляємо "*" (інактивність) та "!" (немає координат) незалежно від порядку
  while (batteryStr.startsWith('*') || batteryStr.startsWith('!')) {
    if (batteryStr.startsWith('*')) {
      batteryStr = batteryStr.substring(1);
    } else if (batteryStr.startsWith('!')) {
      batteryStr = batteryStr.substring(1);
    }
  }
  return batteryStr;
};

const hexToRgba = (hex: string, alpha: number = 0.12): string => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getStatusDisplay = (status: ScooterStatus) => {
  let bgcolor: string;
  let color: string;
  let label: string;
  
  switch (status) {
    case ScooterStatus.AVAILABLE:
      bgcolor = 'rgba(7, 197, 11, 0.12)';
      color = '#07c50b';
      label = 'scooters.statusActiveForRent';
      break;
    case ScooterStatus.CHARGING:
      bgcolor = 'rgba(116, 43, 255, 0.12)';
      color = '#742BFF';
      label = 'scooters.statusOnCharge';
      break;
    case ScooterStatus.TRANSPORTING:
      bgcolor = 'rgba(234, 127, 92, 0.12)';
      color = '#EA7F5C';
      label = 'scooters.statusTransport';
      break;
    case ScooterStatus.MAINTENANCE:
      bgcolor = 'rgba(151, 20, 232, 0.12)';
      color = '#9714E8';
      label = 'scooters.statusInRepair';
      break;
    case ScooterStatus.BROKEN_IN_CITY:
      bgcolor = 'rgba(40, 149, 234, 0.12)';
      color = '#2895EA';
      label = 'scooters.statusBrokenInCity';
      break;
    case ScooterStatus.LOW_BATTERY:
      bgcolor = 'rgba(255, 87, 34, 0.12)';
      color = '#FF5722';
      label = 'scooters.statusLowBattery';
      break;
    case ScooterStatus.HIDDEN:
      bgcolor = 'rgba(117, 117, 117, 0.12)';
      color = '#757575';
      label = 'scooters.statusHidden';
      break;
    default:
      bgcolor = 'rgba(62, 138, 140, 0.12)';
      color = '#3E8A8C';
      label = `scooters.statusRaw_${status}`;
  }
  
  return { label, color, bgcolor };
};

const ScootersPage2 = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const checkPermission = usePermissions();
  
  const scooters = useSelector(selectScooters);
  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);
  const { sendSocketMessage } = useWebSocketContext();

  const isSuperAdmin = auth.info?.type === 'superAdmin';

  const [searchText, setSearchText] = useState('');
  const [isLowBatteryChecked, setIsLowBatteryChecked] = useState(false);
  const [lowBatteryLevel, setLowBatteryLevel] = useState(20);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ column: 'name' | 'status' | 'battery' | 'rating' | null, order: 'asc' | 'desc' }>({ column: null, order: 'asc' });
  const [mapFilters, setMapFilters] = useState<MapFilters>({});
  
  const [scooterSettingsModal, setScooterSettingsModal] = useState<{
    open: boolean;
    scooter?: Scooter;
  }>({ open: false });

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<{
    element: HTMLElement | null;
    scooterId: string;
  }>({ element: null, scooterId: '' });

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const isWaitingForUpdateRef = useRef(false);

  const [selectedScooters, setSelectedScooters] = useState<string[]>([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // WebSocket data fetching
  const getScootersBySockets = useCallback(() => {
    if (!checkPermission('scooters') || !sendSocketMessage) {
      return;
    }
    
    const filters: any = {
      cityId: admins.currentCity
    };
    
    if (searchText) {
      filters.name = searchText;
    }
    
    if (isLowBatteryChecked) {
      filters.lt_battery = lowBatteryLevel;
    }
    
    // Apply map filters
    if (mapFilters.free !== undefined) {
      filters.free = mapFilters.free;
    }
    if (mapFilters.status) {
      filters.status = mapFilters.status;
    }
    if (mapFilters.offline) {
      filters.offline = mapFilters.offline;
    }
    if (mapFilters.unlocked) {
      filters.unlocked = mapFilters.unlocked;
    }
    
    sendSocketMessage('scooters', filters);
  }, [
    checkPermission,
    searchText,
    isLowBatteryChecked,
    lowBatteryLevel,
    admins.currentCity,
    sendSocketMessage,
    mapFilters
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      getScootersBySockets();
    }, 5000);
    getScootersBySockets();
    return () => clearInterval(intervalId);
  }, [getScootersBySockets]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchText, isLowBatteryChecked, lowBatteryLevel, mapFilters]);

  // Відстежуємо оновлення списку самокатів після зміни статусу
  useEffect(() => {
    if (isWaitingForUpdateRef.current && scooters?.items) {
      // Дані оновились, приховуємо лоудер
      setIsUpdatingStatus(false);
      isWaitingForUpdateRef.current = false;
    }
  }, [scooters?.items]);

  const changePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const signalScooter = useCallback(
    (scooter: Scooter) => async () => {
      await dispatch(signalScooterAsync(scooter._id, { value: true }) as any);
    },
    [dispatch]
  );

  const handleSort = (column: 'name' | 'status' | 'battery' | 'rating') => {
    setSortConfig(prevConfig => ({
      column,
      order: prevConfig.column === column && prevConfig.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, scooterId: string) => {
    setStatusMenuAnchor({ element: event.currentTarget, scooterId });
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor({ element: null, scooterId: '' });
  };

  const handleStatusChange = async (newStatus: ScooterStatus) => {
    if (statusMenuAnchor.scooterId) {
      try {
        // Показуємо лоудер тільки якщо вибране місто
        if (admins.currentCity) {
          setIsUpdatingStatus(true);
          isWaitingForUpdateRef.current = true;
        }
        handleStatusMenuClose();
        
        await dispatch(
          updateScooterStatusAsync(statusMenuAnchor.scooterId, { status: newStatus }) as any
        );
        
        // Даємо бекенду час оновити дані
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Оновлюємо список з правильними фільтрами тільки якщо вибране місто
        if (admins.currentCity) {
          getScootersBySockets();
        }
      } catch (error) {
        console.error('Error updating scooter status:', error);
        setIsUpdatingStatus(false);
        isWaitingForUpdateRef.current = false;
      }
    }
  };

  const handleToggleScooterSelection = useCallback((scooterId: string) => {
    setSelectedScooters(prev =>
      prev.includes(scooterId)
        ? prev.filter(id => id !== scooterId)
        : [...prev, scooterId]
    );
  }, []);

  const handleDownloadQR = async (scooter: Scooter) => {
    try {
      const scooterNumber = extractScooterNumber(scooter.name);
      const qrDataUrl = await generateSquareQRWithText(scooterNumber);
      
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `${scooterNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const handleDownloadAllQR = async () => {
    try {
      const scootersToExport = allFilteredScooters.length > 0 ? allFilteredScooters : scooters?.items || [];
      
      if (scootersToExport.length === 0) {
        alert(t('scooters.noScootersForQr'));
        return;
      }

      const zip = new JSZip();
      
      for (const scooter of scootersToExport) {
        const scooterNumber = extractScooterNumber(scooter.name);
        const qrDataUrl = await generateSquareQRWithText(scooterNumber);
        
        const base64Data = qrDataUrl.split(',')[1];
        zip.file(`${scooterNumber}.png`, base64Data, { base64: true });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'scooter_qr_codes.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Failed to generate QR codes archive:', error);
      alert(t('scooters.qrGenerationError'));
    }
  };

  const allFilteredScooters = useMemo(() => {
    let filtered = scooters?.items || [];
    
    if (searchText) {
      filtered = filtered.filter(scooter =>
        scooter.name.toLowerCase().includes(searchText.toLowerCase()) ||
        scooter.id.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (isLowBatteryChecked) {
      filtered = filtered.filter(scooter => {
        const battery = parseInt(cleanBatteryValue(scooter.battery));
        return battery < lowBatteryLevel;
      });
    }

    return [...filtered].sort((a, b) => {
      // Sort by selected column if sortConfig is set
      if (sortConfig.column) {
        let result = 0;
        
        switch (sortConfig.column) {
          case 'name':
            const nameA = cleanScooterName(a.name);
            const nameB = cleanScooterName(b.name);
            
            const numA = parseInt(nameA, 10);
            const numB = parseInt(nameB, 10);
            
            if (!isNaN(numA) && !isNaN(numB)) {
              result = numA - numB;
            } else {
              const lowerA = nameA.toLowerCase();
              const lowerB = nameB.toLowerCase();
              result = lowerA.localeCompare(lowerB);
            }
            break;
            
          case 'status':
            result = (a.status || '').localeCompare(b.status || '');
            break;
            
          case 'battery':
            const batteryA = parseInt(cleanBatteryValue(a.battery));
            const batteryB = parseInt(cleanBatteryValue(b.battery));
            result = batteryA - batteryB;
            break;
            
          case 'rating':
            result = (a.avgRate || 0) - (b.avgRate || 0);
            break;
        }
        
        return sortConfig.order === 'asc' ? result : -result;
      }
      
      // Default: hidden scooters at the bottom
      if (a.status === ScooterStatus.HIDDEN && b.status !== ScooterStatus.HIDDEN) {
        return 1;
      }
      if (a.status !== ScooterStatus.HIDDEN && b.status === ScooterStatus.HIDDEN) {
        return -1;
      }
      return 0;
    });
  }, [scooters.items, searchText, isLowBatteryChecked, lowBatteryLevel, sortConfig]);

  const paginatedScooters = useMemo(() => {
    const startIndex = (page - 1) * LIMIT;
    return allFilteredScooters.slice(startIndex, startIndex + LIMIT);
  }, [allFilteredScooters, page]);

  const paginationCount = useMemo(
    () => Math.max(1, Math.ceil(allFilteredScooters.length / LIMIT)),
    [allFilteredScooters.length]
  );

  const handleSelectAllOnPage = useCallback(() => {
    const pageIds = paginatedScooters.map(s => s._id);
    const allSelected = pageIds.every(id => selectedScooters.includes(id));
    if (allSelected) {
      setSelectedScooters(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedScooters(prev => [...new Set([...prev, ...pageIds])]);
    }
  }, [paginatedScooters, selectedScooters]);

  const handleBulkStatusChange = useCallback(async (newStatus: ScooterStatus) => {
    if (selectedScooters.length === 0) return;

    setBulkUpdating(true);
    try {
      for (const scooterId of selectedScooters) {
        await dispatch(
          updateScooterStatusAsync(scooterId, { status: newStatus }) as any
        );
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      getScootersBySockets();
      setSelectedScooters([]);
      setBulkStatusDialogOpen(false);
    } catch (error) {
      console.error('Error bulk updating status:', error);
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedScooters, dispatch, getScootersBySockets]);

  const renderSortIcon = (column: 'name' | 'status' | 'battery' | 'rating') => {
    if (sortConfig.column !== column) return null;
    return sortConfig.order === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  return (
    <Root>
      <Box sx={{ mb: 3 }}>
        <Title>{t('scooters.title')}</Title>
      </Box>

      <Box sx={{ mb: 3 }}>
        <ScooterCounters 
          mapFilters={mapFilters}
          onFilterSet={setMapFilters}
        />
      </Box>

      <FilterBar>
        <TextField
          placeholder={t('scooters.searchPlaceholder')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{ 
            minWidth: 280,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isLowBatteryChecked}
              onChange={(e) => setIsLowBatteryChecked(e.target.checked)}
            />
          }
          label={t('scooters.lowBattery', { level: lowBatteryLevel })}
        />

        {isLowBatteryChecked && (
          <TextField
            type="number"
            value={lowBatteryLevel}
            onChange={(e) => setLowBatteryLevel(Number(e.target.value))}
            size="small"
            sx={{ 
              width: 100,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        )}

        <Button
          variant="contained"
          startIcon={<QrCode2 />}
          onClick={handleDownloadAllQR}
          disabled={!scooters?.items || scooters.items.length === 0}
          sx={{
            bgcolor: '#5073F8',
            color: '#FFF',
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            marginLeft: 'auto',
            '&:hover': {
              bgcolor: '#3D5FCC'
            }
          }}
        >
          {t('scooters.downloadAllQr')}
        </Button>
      </FilterBar>

      {selectedScooters.length > 0 && (
        <Box sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: theme.palette.mode === 'light' ? 'rgba(78, 205, 196, 0.08)' : 'rgba(78, 205, 196, 0.15)',
          border: `1px solid rgba(78, 205, 196, 0.3)`
        }}>
          <CheckBoxIcon sx={{ color: '#4ECDC4' }} />
          <Typography sx={{ fontWeight: 600 }}>
            {t('scooters.selected')}: {selectedScooters.length}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setBulkStatusDialogOpen(true)}
            sx={{
              backgroundColor: '#4ECDC4',
              '&:hover': { backgroundColor: '#3BA99F' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            {t('scooters.changeStatus')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedScooters([])}
            startIcon={<Close />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              ml: 'auto'
            }}
          >
            {t('scooters.cancelSelection')}
          </Button>
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
        <TableContainer component={Paper} sx={{ 
          borderRadius: 3,
          border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`
        }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell sx={{ width: 50, cursor: 'default', '&:hover': { backgroundColor: 'inherit' } }} onClick={handleSelectAllOnPage}>
                <Checkbox
                  size="small"
                  checked={paginatedScooters.length > 0 && paginatedScooters.every(s => selectedScooters.includes(s._id))}
                  indeterminate={paginatedScooters.some(s => selectedScooters.includes(s._id)) && !paginatedScooters.every(s => selectedScooters.includes(s._id))}
                  sx={{ color: '#4ECDC4', '&.Mui-checked': { color: '#4ECDC4' }, '&.MuiCheckbox-indeterminate': { color: '#4ECDC4' } }}
                />
              </TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('name')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('scooters.colName')}
                  {renderSortIcon('name')}
                </Box>
              </TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('status')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('scooters.colStatus')}
                  {renderSortIcon('status')}
                </Box>
              </TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('battery')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('scooters.colBattery')}
                  {renderSortIcon('battery')}
                </Box>
              </TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('rating')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('scooters.colRating')}
                  {renderSortIcon('rating')}
                </Box>
              </TableHeaderCell>
              <TableHeaderCell align="right">{t('scooters.colActions')}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedScooters.map(scooter => {
              const statusDisplay = getStatusDisplay(scooter.status as ScooterStatus);
              const batteryValue = parseInt(cleanBatteryValue(scooter.battery));
              const isAVTOScooter = isAVTO(scooter.name);
              const hasGPS = hasGPSIssue(scooter.name);
              const hasInactivity = hasInactivityFlag(scooter.battery);
              const noCoordinates = hasNoCoordinates(scooter.battery);
              
              return (
                <StyledTableRow key={scooter._id}>
                  <StyledTableCell sx={{ width: 50 }}>
                    <Checkbox
                      size="small"
                      checked={selectedScooters.includes(scooter._id)}
                      onChange={() => handleToggleScooterSelection(scooter._id)}
                      sx={{ color: '#4ECDC4', '&.Mui-checked': { color: '#4ECDC4' } }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Tooltip title={isAVTOScooter ? t('scooters.busTracker') : getDotTooltip(scooter)}>
                        <Avatar
                          onClick={() => setScooterSettingsModal({ open: true, scooter })}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isAVTOScooter 
                              ? 'rgba(25, 118, 210, 0.12)' 
                              : hexToRgba(getDotColor(scooter)),
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          {isAVTOScooter ? (
                            <DirectionsBus sx={{ color: '#1976D2', fontSize: 24 }} />
                          ) : (
                            <ElectricScooter sx={{ color: getDotColor(scooter), fontSize: 24 }} />
                          )}
                        </Avatar>
                      </Tooltip>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasGPS && (
                            <Tooltip title={t('scooters.noGps')}>
                              <SatelliteAlt sx={{ fontSize: 16, color: '#F44336' }} />
                            </Tooltip>
                          )}
                          <Typography 
                            variant='body1' 
                            fontWeight={600}
                            onClick={() => setScooterSettingsModal({ open: true, scooter })}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': {
                                color: theme.palette.mode === 'light' ? '#424242' : '#5073F8'
                              }
                            }}
                          >
                            {cleanScooterName(scooter.name)}
                          </Typography>
                          {noCoordinates && (
                            <Tooltip title={t('scooters.noCoords')}>
                              <Chip 
                                label={<LocationOff sx={{ fontSize: 12 }} />}
                                size='small' 
                                sx={{ 
                                  height: 18,
                                  minWidth: 24,
                                  padding: '0 4px',
                                  bgcolor: 'rgba(244, 67, 54, 0.12)',
                                  color: '#F44336',
                                  '& .MuiChip-label': {
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                  }
                                }} 
                              />
                            </Tooltip>
                          )}
                          {hasInactivity && (
                            <Tooltip title={t('scooters.notMoving24h')}>
                              <Chip 
                                label='24h' 
                                size='small' 
                                sx={{ 
                                  height: 18,
                                  fontSize: '10px',
                                  bgcolor: 'rgba(255, 152, 0, 0.12)',
                                  color: '#FF9800',
                                  fontWeight: 600
                                }} 
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant='caption' color='text.secondary'>
                          ID: {scooter.id}
                        </Typography>
                      </Box>
                    </Box>
                  </StyledTableCell>

                  <StyledTableCell>
                    <Chip 
                      label={t(statusDisplay.label)}
                      size="small"
                      onClick={(e) => handleStatusClick(e, scooter._id)}
                      sx={{ 
                        backgroundColor: statusDisplay.bgcolor,
                        color: statusDisplay.color,
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }} 
                    />
                  </StyledTableCell>

                  <StyledTableCell>
                    <Box sx={{ width: 120 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {batteryValue}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={batteryValue} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: batteryValue > 50 ? '#4CAF50' : batteryValue > 20 ? '#FF9800' : '#F44336',
                            borderRadius: 3
                          }
                        }} 
                      />
                    </Box>
                  </StyledTableCell>

                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating value={scooter.avgRate || 0} readOnly size="small" precision={0.1} />
                      <Typography variant="caption" color="text.secondary">
                        ({scooter.avgRate?.toFixed(1) || '0.0'})
                      </Typography>
                    </Box>
                  </StyledTableCell>

                  <StyledTableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title={t('scooters.call')}>
                        <IconButton 
                          onClick={signalScooter(scooter)} 
                          size='medium'
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                          }}
                        >
                          <VolumeUp fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={scooter.lock ? t('scooters.locked') : t('scooters.unlocked')}>
                        <IconButton
                          onClick={() =>
                            dispatch(
                              lockScooterAsync(scooter._id, { lock: !scooter.lock }) as any
                            )
                          }
                          size='medium'
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                          }}
                        >
                          {scooter.lock ? <Block fontSize='small' /> : <LockOpen fontSize='small' />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t('scooters.settings')}>
                        <IconButton
                          onClick={() => setScooterSettingsModal({ open: true, scooter })}
                          size='medium'
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                          }}
                        >
                          <Settings fontSize='small' />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t('scooters.downloadQr')}>
                        <IconButton
                          onClick={() => handleDownloadQR(scooter)}
                          size='medium'
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                          }}
                        >
                          <QrCode2 fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

        {isUpdatingStatus && admins.currentCity && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              borderRadius: 3,
              zIndex: 10
            }}
          >
            <CircularProgress size={48} sx={{ color: '#5073F8' }} />
          </Box>
        )}
      </Box>

      {allFilteredScooters.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
          borderRadius: 3,
          mt: 2
        }}>
          <ElectricScooter sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {t('scooters.notFound')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('scooters.tryChangeFilters')}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination 
          count={paginationCount} 
          page={page}
          onChange={changePage}
          size="large"
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: 2,
              fontWeight: 600,
              '&.Mui-selected': {
                backgroundColor: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
                color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#212121' : '#E0E0E0'
                }
              }
            }
          }}
        />
      </Box>

      <ScooterSettingsModal
        open={scooterSettingsModal.open}
        scooter={scooterSettingsModal.scooter}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setScooterSettingsModal({ open: false })}
      />

      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => !bulkUpdating && setBulkStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ElectricScooter sx={{ color: '#4ECDC4' }} />
            <Typography variant="h6">
              {t('scooters.changeStatusCount', { count: selectedScooters.length })}
            </Typography>
          </Box>
          <IconButton onClick={() => setBulkStatusDialogOpen(false)} disabled={bulkUpdating} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {bulkUpdating ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
              <CircularProgress sx={{ color: '#4ECDC4' }} />
              <Typography>{t('scooters.updatingStatuses')}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {Object.values(ScooterStatus).map((status) => {
                const display = getStatusDisplay(status);
                return (
                  <Button
                    key={status}
                    onClick={() => handleBulkStatusChange(status)}
                    variant="outlined"
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: display.color,
                        backgroundColor: display.bgcolor
                      }
                    }}
                  >
                    <Chip
                      label={t(display.label)}
                      size="small"
                      sx={{
                        backgroundColor: display.bgcolor,
                        color: display.color,
                        fontWeight: 600,
                        pointerEvents: 'none'
                      }}
                    />
                  </Button>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={statusMenuAnchor.element}
        open={Boolean(statusMenuAnchor.element)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: theme.palette.mode === 'light' 
              ? '0 4px 20px rgba(0,0,0,0.1)' 
              : '0 4px 20px rgba(0,0,0,0.3)',
          }
        }}
      >
        {Object.values(ScooterStatus).map((status) => {
          const display = getStatusDisplay(status);
          return (
            <MenuItem 
              key={status} 
              onClick={() => handleStatusChange(status)}
              sx={{ 
                minWidth: 200,
                gap: 1.5,
                py: 1.5,
                px: 2
              }}
            >
              <Chip 
                label={t(display.label)}
                size="small"
                sx={{ 
                  backgroundColor: display.bgcolor,
                  color: display.color,
                  fontWeight: 600,
                  pointerEvents: 'none'
                }} 
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Root>
  );
};

export default ScootersPage2;
